import { useState, useEffect, useMemo } from 'react';
import { SearchFilters, SortValue, DurationValue } from '../../../types/search';
import { Topic } from '../../../types/topic';
import { SectionId } from './config';

const INITIAL_TOPICS_SHOWN = 5;
const TOPICS_INCREMENT = 5;

interface UseFilterMenuProps {
  filters: SearchFilters;
  topics: Topic[];
  onChange: (filters: SearchFilters) => void;
}

function searchTopics(topics: Topic[], searchQuery: string): Topic[] {
  if (!searchQuery.trim()) return topics;

  const searchTerm = searchQuery.toLowerCase().trim();
  
  return topics.filter(topic => 
    topic.title.toLowerCase().includes(searchTerm)
  );
}

export const useFilterMenu = ({ filters, topics, onChange }: UseFilterMenuProps) => {
  const [draftFilters, setDraftFilters] = useState<SearchFilters>(filters);
  const [topicSearch, setTopicSearch] = useState('');
  const [numTopicsShown, setNumTopicsShown] = useState(INITIAL_TOPICS_SHOWN);
  const [sectionsCollapsed, setSectionsCollapsed] = useState<Record<SectionId, boolean>>({
    topics: true,
    duration: true,
    sort: true,
    broadcastStatus: true
  });

  useEffect(() => {
    setDraftFilters(filters);
  }, [filters]);

  const filteredTopics = useMemo(() => 
    searchTopics(topics, topicSearch),
    [topics, topicSearch]
  );

  const hasMoreTopics = numTopicsShown < filteredTopics.length;
  const hasLessTopics = numTopicsShown > INITIAL_TOPICS_SHOWN;

  const handleShowMore = () => {
    setNumTopicsShown(prev => Math.min(prev + TOPICS_INCREMENT, filteredTopics.length));
  };

  const handleShowLess = () => {
    setNumTopicsShown(INITIAL_TOPICS_SHOWN);
  };

  const handleTopicToggle = (topicId: string) => {
    const newFilters = {
      ...draftFilters,
      topics: draftFilters.topics.includes(topicId)
        ? draftFilters.topics.filter(id => id !== topicId)
        : [...draftFilters.topics, topicId]
    };
    setDraftFilters(newFilters);
    onChange(newFilters);
  };

  const handleDurationToggle = (duration: string) => {
    const newFilters = {
      ...draftFilters,
      duration: draftFilters.duration === duration ? undefined : duration
    };
    setDraftFilters(newFilters);
    onChange(newFilters);
  };

  const handleSortChange = (sort: SortValue) => {
    const newFilters: SearchFilters = {
      ...draftFilters,
      sortBy: sort,
      sortType: 'desc' as const
    };
    setDraftFilters(newFilters);
    onChange(newFilters);
  };

  const handleSortOrderChange = (sortType: 'asc' | 'desc') => {
    const newFilters = {
      ...draftFilters,
      sortType
    };
    setDraftFilters(newFilters);
    onChange(newFilters);
  };

  const handleBroadcastStatusToggle = (status: string) => {
    const newFilters = {
      ...draftFilters,
      broadcastStatus: draftFilters.broadcastStatus?.includes(status)
        ? draftFilters.broadcastStatus.filter(s => s !== status)
        : [...(draftFilters.broadcastStatus || []), status]
    };
    setDraftFilters(newFilters);
    onChange(newFilters);
  };

  const toggleSection = (section: SectionId) => {
    setSectionsCollapsed(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return {
    draftFilters,
    sectionsCollapsed,
    topicSearch,
    numTopicsShown,
    filteredTopics,
    hasMoreTopics,
    hasLessTopics,
    handleTopicToggle,
    handleDurationToggle,
    handleSortChange,
    handleSortOrderChange,
    handleBroadcastStatusToggle,
    handleShowMore,
    handleShowLess,
    setTopicSearch,
    toggleSection
  };
}; 